import { z } from 'zod'
import { MATRIX_LABELS } from '../constants/matrices'

// ─────────────────────────────────────────────
//  Редактирование оформленного заказа (29.09.2026)
// ─────────────────────────────────────────────
// Паспорт — docs/features/order-editing.md. Менять состав можно до статуса
// «Оплачен» (ORDER_EDITABLE_STATUSES). Позиции заказа — это позиции заявки в
// «Счёте», поэтому правка идёт в позицию заявки и в строки действующих счетов.

// eslint-disable-next-line no-control-regex -- намеренно: запрет управляющих символов на входе
const NO_CONTROL_CHARS = /^[^\x00-\x08\x0B\x0C\x0E-\x1F\x7F]*$/
const SAFE_STRING = (max: number) =>
  z.string().max(max).regex(NO_CONTROL_CHARS, 'Недопустимые управляющие символы')

// Границы — как у цены и количества позиции заявки (request.types.ts).
const PRICE = z.number().finite().nonnegative().max(1_000_000_000)
const QTY = z.number().finite().positive().max(10_000_000)
const PART_NUMBER = SAFE_STRING(200).refine((v) => v.trim().length > 0, {
  message: 'Артикул не может быть пустым',
})

/** Правка позиции заказа: артикул, бренд, количество, цена клиенту (₽ за единицу). */
export const UpdateOrderPositionSchema = z
  .object({
    partNumber: PART_NUMBER.optional(),
    brand: SAFE_STRING(100).nullable().optional(),
    quantity: QTY.optional(),
    price: PRICE.optional(),
  })
  .refine(
    (d) =>
      d.partNumber !== undefined ||
      d.brand !== undefined ||
      d.quantity !== undefined ||
      d.price !== undefined,
    { message: 'Укажите, что изменить в позиции заказа' },
  )
export type UpdateOrderPositionDto = z.infer<typeof UpdateOrderPositionSchema>

/**
 * Исход для позиции, убранной из заказа: «Отказ клиента» (с причиной, заявка
 * остаётся «в заказе») или возврат в «КП выставлено» (позицию можно снова
 * предложить, заявка переоткрывается).
 */
export const OrderRemoveOutcomeSchema = z.enum(['REJECTED', 'BACK_TO_KP'])
export type OrderRemoveOutcome = z.infer<typeof OrderRemoveOutcomeSchema>

export const RemoveOrderPositionsSchema = z.object({
  requestItemIds: z.array(z.string().uuid()).min(1).max(500),
  outcome: OrderRemoveOutcomeSchema,
  /** Причина отказа — обязательна для REJECTED (не короче 3 символов, проверка в сервисе). */
  reason: SAFE_STRING(2000).nullable().optional(),
})
export type RemoveOrderPositionsDto = z.infer<typeof RemoveOrderPositionsSchema>

/** Добавить в заказ позиции заявки из КП. invoiceId — в какой счёт; null — без счёта. */
export const AddOrderPositionsSchema = z.object({
  requestItemIds: z.array(z.string().uuid()).min(1).max(500),
  invoiceId: z.string().uuid().nullable().optional(),
})
export type AddOrderPositionsDto = z.infer<typeof AddOrderPositionsSchema>

/**
 * Новая позиция вручную (её не было в заявке): сразу в «Счёт» с ценой клиенту.
 * Матрица, поставщик и себестоимость — по желанию; поставщик и себестоимость
 * записываются квотой в выбранной матрице (без матрицы не принимаются).
 */
export const CreateOrderPositionSchema = z
  .object({
    partNumber: PART_NUMBER,
    brand: SAFE_STRING(100).nullable().optional(),
    quantity: QTY,
    unit: SAFE_STRING(20).nullable().optional(),
    price: PRICE,
    /** Сегмент («Наименование»): пусто — определим по справочнику и правилам. */
    category: SAFE_STRING(100).nullable().optional(),
    matrix: z.enum(MATRIX_LABELS).nullable().optional(),
    supplierName: SAFE_STRING(255).nullable().optional(),
    costPrice: PRICE.nullable().optional(),
    costCurrency: z.enum(['RUB', 'USD', 'EUR', 'CNY']).nullable().optional(),
    invoiceId: z.string().uuid().nullable().optional(),
  })
  .refine((d) => d.matrix || (d.costPrice == null && !d.supplierName?.trim()), {
    message: 'Поставщик и себестоимость указываются вместе с матрицей',
    path: ['matrix'],
  })
export type CreateOrderPositionDto = z.infer<typeof CreateOrderPositionSchema>

/** Позиция заявки, которую можно добавить в заказ (её нет в «Счёте»). */
export interface AddableOrderPositionDto {
  requestItemId: string
  /** № позиции в заявке (как в КП и в таблице заявки). */
  position: number
  partNumber: string
  brand: string | null
  category: string | null
  quantity: number
  unit: string | null
  /** Текущий статус позиции заявки (код RequestItemStatus). */
  status: string
  /** Цена клиенту из последнего КП с этой позицией; null — позиции нет ни в одном КП. */
  kpPrice: number | null
  quotationId: string | null
  quotationCreatedAt: string | null
}
